import { useCallback } from 'react';

/** Custom-event properties accepted by Plausible. */
export type PlausibleProps = Record<string, string | number | boolean>;

export interface PlausibleEventOptions {
  /** Custom properties to attach to the event. */
  props?: PlausibleProps;
  /** Callback run after the event is logged. */
  callback?: () => void;
  /** Whether the event counts toward bounce-rate (default true). */
  interactive?: boolean;
  /** Revenue attribution, for ecommerce-style events. */
  revenue?: { currency: string; amount: number };
}

type PlausibleFn = ((eventName: string, options?: PlausibleEventOptions) => void) & {
  q?: unknown[][];
  o?: unknown;
  init?: (options?: unknown) => void;
};

declare global {
  interface Window {
    plausible?: PlausibleFn;
  }
}

/**
 * Load Plausible when `VITE_PLAUSIBLE_SRC` is configured. Mirrors the official
 * `pa-` snippet: defines the queue stub so events fired before the async
 * script loads are buffered, then calls `init()`. No-op when the env var is
 * unset (so dev / e2e / any analytics-less build does nothing) and idempotent.
 *
 * Frontend-only: there is no BFF proxy — the script talks to Plausible directly.
 */
export function initPlausible(): void {
  const src = import.meta.env.VITE_PLAUSIBLE_SRC;
  if (!src || typeof document === 'undefined') return;
  if (document.querySelector('script[data-plausible]')) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  script.setAttribute('data-plausible', '');
  document.head.appendChild(script);

  const w = window;
  w.plausible =
    w.plausible ??
    (((...args: unknown[]) => {
      (w.plausible!.q = w.plausible!.q ?? []).push(args);
    }) as PlausibleFn);
  w.plausible.init = w.plausible.init ?? ((options?: unknown) => {
    w.plausible!.o = options ?? {};
  });
  w.plausible.init();
}

/**
 * Track a custom Plausible event. No-op (with a dev-console note) when
 * Plausible isn't loaded, so call sites never need to guard.
 *
 * @example
 *   const track = usePlausible();
 *   track('Org Created', { props: { source: 'dashboard' } });
 */
export const usePlausible = () => {
  return useCallback((eventName: string, options?: PlausibleEventOptions) => {
    if (typeof window.plausible === 'undefined') {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[Plausible] (dev, not loaded) event:', eventName, options);
      }
      return;
    }
    window.plausible(eventName, options);
  }, []);
};
